import { Box, SelectChangeEvent, Stack, Snackbar, Alert } from "@mui/material";
import FinancialExportTable from "./FinancialExportTable";
import { PaginationView } from "../PaginationView";
import { useState, useEffect } from "react";
import FilterSection from "./FilterSection";
import { FinancialExportStyles } from "./styles";
import useFinanceExportData from "./useFinanceExportData";
import { DownloadProps, FinanceExportRequestData } from "./type";

const FinancialExport = ({ dashboardId }: { dashboardId: number }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    loading,
    error,
    tableData,
    fetchFinanceExportData,
    generateExport,
    handleExport,
    downloadingItems,
    totalCount,
  } = useFinanceExportData(dashboardId);

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number.parseInt(String(event.target.value), 10));
    setCurrentPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    fetchFinanceExportData({
      page_size: rowsPerPage,
      page: currentPage,
    });
  }, [currentPage, rowsPerPage, fetchFinanceExportData, refreshKey]);

  const handleGenerateExport = async (
    requestData: FinanceExportRequestData,
  ) => {
    try {
      await generateExport(requestData);
      setCurrentPage(1);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.response?.data?.detail ??
          error?.message ??
          "Failed to generate export. Please try again.",
      );
      setOpenErrorSnackbar(true);
      throw error;
    }
  };

  const handleCloseErrorSnackbar = () => {
    setOpenErrorSnackbar(false);
  };

  const handleDownloadClick = async (item: DownloadProps) => {
    try {
      await handleExport(
        item.id,
        item.export_data_type,
        item.from_date,
        item.to_date,
      );
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to download file. Please try again.",
      );
      setOpenErrorSnackbar(true);
    }
  };

  return (
    <Stack direction="column" px={3} py={2}>
      <Box sx={FinancialExportStyles.filterSection}>
        <FilterSection
          handleGenerateExport={handleGenerateExport}
          dashboardId={dashboardId}
        />
      </Box>
      <Box sx={FinancialExportStyles.tableMarginTop}>
        <FinancialExportTable
          data={tableData}
          loading={loading}
          onDownloadClick={handleDownloadClick}
          errorMessage={error?.message}
          onRefresh={handleRefresh}
          downloadingItems={downloadingItems}
        />
      </Box>
      <Box sx={FinancialExportStyles.paginationMarginTop}>
        <PaginationView
          count={totalCount}
          page={currentPage}
          handlePageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          handleRowsPerchange={handleRowsPerPageChange}
          disabled={!tableData.length}
        />
      </Box>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          variant="filled"
          sx={FinancialExportStyles.errorSnackbar}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default FinancialExport;
